/**
 * Auth bridge — lets the plain (non-React) API layer recover from 401s.
 *
 * What: `fetchWithConfig` (src/services/apiUtils.ts) is a module function with
 * no access to React or the Auth0 SDK, yet it's the single choke point every
 * authenticated REST call funnels through. This bridge lets the `AuthProvider`
 * hand it two capabilities once on mount — force-refresh the ID token, and send
 * the user to login — so a 401 on ANY surface self-heals or cleanly bounces to
 * login instead of dead-ending on an error banner.
 * Why: Before this, only the practice surfaces (via `useAuthenticatedCall`) had
 * reactive 401 recovery; homework, teacher, and admin surfaces surfaced a raw
 * "session expired" error with no redirect. See `apiUtils.ts` for the interceptor.
 * When: Update alongside the `fetchWithConfig` 401 interceptor or the
 * `AuthProvider` registration.
 *
 * Both operations are de-duplicated: a burst of parallel 401s shares ONE token
 * refresh (so a rotating refresh token is never spent twice) and triggers AT
 * MOST ONE login redirect per page load.
 */

export interface AuthBridge {
  /**
   * Force-refresh the Auth0 ID token. Resolves to the fresh raw token, or
   * `undefined` when the session can't be refreshed (dead/revoked refresh
   * token, offline). Must never reject.
   */
  refresh: () => Promise<string | undefined>;
  /** Send the user to the login flow, preserving `returnTo` for post-login. */
  redirect: (returnTo: string) => void;
}

let bridge: AuthBridge | null = null;
let inFlightRefresh: Promise<string | undefined> | null = null;
let hasRedirected = false;

/** Registered once by `AuthProvider`. Re-registering overwrites the handlers. */
export function registerAuthBridge(next: AuthBridge): void {
  bridge = next;
}

/** Test-only: clear the registered bridge and the de-dup / redirect state. */
export function resetAuthBridge(): void {
  bridge = null;
  inFlightRefresh = null;
  hasRedirected = false;
}

export function isAuthBridgeRegistered(): boolean {
  return bridge !== null;
}

/**
 * Force-refresh the token, sharing a single in-flight refresh across concurrent
 * callers so parallel 401s don't each spend a rotating refresh token. Resolves
 * to `undefined` when no bridge is registered or the refresh fails.
 */
export function refreshAuthToken(): Promise<string | undefined> {
  const active = bridge;
  if (!active) return Promise.resolve(undefined);
  if (!inFlightRefresh) {
    inFlightRefresh = Promise.resolve()
      .then(() => active.refresh())
      .catch(() => undefined)
      .finally(() => {
        inFlightRefresh = null;
      });
  }
  return inFlightRefresh;
}

/**
 * Redirect to login at most once per page load. Subsequent calls (e.g. from a
 * burst of parallel 401s) are no-ops — the redirect navigates away and the
 * page reloads with a fresh session, resetting this flag.
 */
export function triggerReauthRedirect(returnTo?: string): void {
  if (!bridge || hasRedirected) return;
  hasRedirected = true;
  const target =
    returnTo ??
    (typeof window !== 'undefined'
      ? window.location.pathname + window.location.search
      : '/');
  bridge.redirect(target);
}
